import { useState, useEffect } from "react";
import { ReactComponent as PaywallWandIcon } from "@images/paywall_benefit_wand_icon.svg";
import { ReactComponent as PaywallMoonIcon } from "@images/paywall_benefit_moon_icon.svg";
import { ReactComponent as PaywallTextIcon } from "@images/paywall_benefit_text_icon.svg";
import { ReactComponent as PaywallBookmarkIcon } from "@images/bookmark_on_icon.svg";
import { ReactComponent as PaywallOfflineIcon } from "@images/paywall_benefit_offline_icon.svg";
import { ReactComponent as UnlockIcon } from "@images/unlock_icon.svg";
import { ReactComponent as BellIcon } from "@images/bell_icon.svg";
import { ReactComponent as StarIcon } from "@images/star_icon.svg";
import { ReactComponent as AudioIcon } from "@images/tabbar_audio.svg";
import Alert, { AlertState } from "@components/common/Alert";
import { NLCreateCheckout, NLPurchaseProduct } from "@models/Models";
import { recordEvent } from "@utils/managers/AnalyticsManager";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCheckoutUrl } from "@utils/managers/networking/NetworkManager";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { yearlyPlan, monthlyPlan } from "@utils/constants/PaywallConstants";
import Timeline from "@components/common/Timeline";
import DeleteUserModal from "@components/settings/delete-user/DeleteUserModal";
import NewMailboxModal from "@components/settings/connected-mailboxes/NewMailboxModal";
import { useOnboardingProfile } from "@providers/OnboardingContext";

interface LocationState {
	isOauth: string;
}

const OnboardingPaywall = () => {
	const location = useLocation();
	const isOauth = (location.state as LocationState)?.isOauth ?? null;
	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
	const [selectedProduct, setSelectedProduct] = useState<NLPurchaseProduct>(yearlyPlan);
	const [isCreatingCheckout, setIsCreatingCheckout] = useState<boolean>(false);
	const [isShowDeleteUser, setIsShowDeleteUser] = useState<boolean>(false);
	const [isShowTrial, setIsShowTrial] = useState<boolean>(true);
	const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();
	useEffect(() => {
		recordEvent(kAnalyticsConstants.Paywall.paywallPresented, { reference: kAnalyticsConstants.Reference.onboarding });
		var tempOnboardingProfile = { ...onboardingProfile! };
		tempOnboardingProfile.persistence.hasSeenPaywall = true;
		setOnboardingProfile(tempOnboardingProfile);
	}, []);

	const purchaseProduct: NLPurchaseProduct[] = [yearlyPlan, monthlyPlan];
	const ProBenefit = ({ icon, title, isAsterisk = false }) => {
		return (
			<div className="flex flex-row transition ease-in-out gap-2 items-center">
				<div className="pr-2">{icon}</div>
				<div className="flex-col text-left">
					<div className="text-white font-regular font-primary text-base">
						{title}
						{isAsterisk && <span>&#42;</span>}
					</div>
				</div>
			</div>
		);
	};

	const productOnSelect = (product: NLPurchaseProduct) => {
		setSelectedProduct(product);
	};

	const subscribeOnClick = () => {
		recordEvent(kAnalyticsConstants.Paywall.subscribeButtonTapped, { reference: kAnalyticsConstants.Reference.onboarding, price_id: selectedProduct.price_id });
		setIsCreatingCheckout(true);
		let successUrl = window.location.origin + "/get-started/welcome";
		const createCheckout = new NLCreateCheckout(selectedProduct.price_id, successUrl, window.location.href);
		getCheckoutUrl(createCheckout)
			.then((checkout) => {
				window.location.href = checkout.checkout_url;
			})
			.catch((error) => {
				recordEvent(kAnalyticsConstants.Paywall.checkoutGenerationError, { reference: kAnalyticsConstants.Reference.onboarding, price_id: selectedProduct.price_id, error: error.message });
				setIsCreatingCheckout(false);
				setAlertState({ isShow: true, title: "Uh oh! Something went wrong", message: "Please try again and contact us if the problem persists", actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
			});
	};

	const milestones = [
		{
			title: "Today: Free trial begins",
			description: "Full access to everything Meco has to offer - no strings attached.",
			Icon: UnlockIcon,
			isCompleted: true,
		},
		{
			title: "Day 5: Trial reminder",
			description: "We'll send you an email reminder that your trial is ending soon.",
			Icon: BellIcon,
			isCompleted: false,
		},
		{
			title: "Day 7: Trial ends",
			description: "Loving Meco? Do nothing to continue. Or cancel anytime before, no pressure.",
			Icon: StarIcon,
			isCompleted: false,
		},
	];

	useEffect(() => {
		setIsShowTrial(selectedProduct.trial_days !== undefined);
	}, [selectedProduct])

	return (
		<div>
			<ParticlesBackground />
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<DeleteUserModal isShow={isShowDeleteUser} isPaywall={true} onClose={() => setIsShowDeleteUser(false)} />
			<div className="flex min-h-[100dvh] flex-col md:justify-center justify-start overflow-hidden py-2 md:py-12">
				<div className="flex flex-col gap-10 items-center justify-center p-3">
					<div className={`meco_container text-center max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]`}>
						{isShowTrial && <div className="flex flex-col gap-3 mb-5 border border-dashed border-white/20 p-5 rounded-xl">
							<div className="text-white font-bold font-primary text-3xl md:text-center md:leading-10 leading-9">We offer 7 days free so everyone can try Meco</div>
							<Timeline milestones={milestones} />
						</div> }
						<div className="flex flex-col md:flex-row gap-5 max-w-[400px] md:max-w-none">
							<div className="w-full md:w-1/2 flex flex-col gap-[50px]">
								<div className="text-white font-bold font-primary text-3xl md:text-left md:leading-10 leading-9">{isOauth !== null ? `Complete your ${isOauth} setup with Meco PRO` : "Free your inbox & blaze through newsletters with PRO"}</div>
								<div className="flex flex-col gap-3 m-auto md:m-0">
									<ProBenefit icon={<PaywallWandIcon className="fill-white w-[20px]" />} title="Connect your Gmail & Outlook mailboxes" />
									<ProBenefit icon={<AudioIcon className="fill-white w-[20px]" />} title="Personalised podcasts made for you" />
									<ProBenefit icon={<PaywallMoonIcon className="fill-white w-[20px]" />} title="Four color themes including Dark Mode" />
									<ProBenefit icon={<PaywallBookmarkIcon className="fill-white w-[14px] ml-[3px]" />} title="Save links, highlight learnings & add notes" isAsterisk={true} />
									<ProBenefit icon={<PaywallOfflineIcon className="fill-white w-[20px]" />} title="Read anywhere with offline access" isAsterisk={true} />
									<div className="font-regular font-primary text-2xs text-white/50 text-left">
										<span>&#42;</span> Features are currently iOS & Android only and coming to web soon.
									</div>
								</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-4 pt-3">
								<div className="flex flex-col gap-2 w-full">
									{purchaseProduct.map((product) => {
										return (
											<div key={product.price_id} className={`relative flex flex-col ${product.price_id === selectedProduct.price_id ? "border-success-green" : "border-white/20"} bg-white/10 hover:bg-white/20 transition ease-in-out border duration-300 rounded-xl px-3 py-4 w-full gap-2 items-start mb-[12px] shadow-md cursor-pointer`} onClick={() => productOnSelect(product)}>
												{product.badge && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-2 bg-success-green text-white font-medium font-primary text-xs rounded-full">{product.badge}</div>}
												<div className="flex flex-row items-center justify-between w-full gap-3">
													<div className="flex flex-col items-start text-left flex-grow font-medium font-primary text-lg text-white">{product.product_name}</div>
													<div className="flex flex-row gap-2 items-center">
														<div className="font-medium font-primary text-base text-white">
															{product.monthly_price}
															<div className="inline font-regular text-xs pl-1">/MO</div>
														</div>
														<input readOnly className={`checkbox-round green`} type="checkbox" name="selectable-sender" checked={product.price_id === selectedProduct.price_id} value={product.price_id} />
													</div>
												</div>
											</div>
										);
									})}
								</div>
								<div className="flex flex-col gap-2 w-full items-center">
									<button onClick={() => subscribeOnClick()} className="flex cta-button w-full shadow-2xl">
										{isCreatingCheckout ? <LoadingSpinner className="h-[26px] text-white/20 animate-spin fill-white" /> : selectedProduct.trial_days ? "Start My " + selectedProduct.trial_days + "-Day Free Trial" : "Subscribe to unlock"}
									</button>
									<div className={`font-regular font-primary text-sm text-white/50 ${selectedProduct.trial_days ? "opacity-100" : "opacity-0"}`}>
										Free for {selectedProduct.trial_days} days, then {selectedProduct.total_price}/{selectedProduct.term}. Cancel anytime.
									</div>
									<Link to="/get-started/welcome" className="md:hidden">
										<button className="text-force-primary-light font-medium font-primary text-base focus:outline-none whitespace-nowrap mt-3">{isOauth !== null ? "See other integrations" : "Not now"}</button>
									</Link>
								</div>
							</div>
						</div>
						<div className="w-full hidden md:block">
							<div className="h-[1px] w-full bg-force-primary-light/20 mt-5"></div>
								<div className="flex justify-end">
									<Link to={isOauth !== null ? "/get-started/integrations" : "/get-started/welcome"}>
										<button className="text-force-primary-light font-medium font-primary text-base focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-white/20 hover:bg-brand-blue/80 transition ease-in-out whitespace-nowrap mt-3">{isOauth !== null ? "See other integrations" : "Not now"}</button>
									</Link>
								</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OnboardingPaywall;
